import { initReactI18next } from 'react-i18next'
import i18n from 'i18next'
import { deepMerge } from '@transsion/utils'
import { handle, store } from '@/utils'
import { ImportMetaGlob } from '@/types/base'

type PromiseArr = Promise<Record<'default', Record<string, unknown>>>[]

const localI18n = import.meta.glob('./locale/**/*.json') as ImportMetaGlob
const defaultLocalePaths = ['./locale/en/base.json']
const STORE_LANG_KEY = 'tomee_m_lang'

function loadLocalI18nJson(name: string): PromiseArr {
  const promises: PromiseArr = []

  const reg = new RegExp(`^\\.\\/locale\\/${name}\\/(.*)\\.json$`)

  for (const p in localI18n) {
    if (reg.test(p)) {
      promises.push(localI18n[p]())
    }
  }

  if (promises.length === 0) {
    promises.push(...defaultLocalePaths.map((p) => localI18n[p]()))
  }

  return promises
}

const getLocale = () => {
  const locale = handle.getUrlParamValue('lang') || store.getStore(STORE_LANG_KEY, 'local') || 'en'
  if (locale.indexOf('en') > -1) {
    return 'en-US'
  }
  if (locale.indexOf('zh') > -1) {
    return 'zh-CN'
  }
  if (locale.indexOf('fr') > -1) {
    return 'fr-FR'
  }
  if (locale.indexOf('ar') > -1) {
    return 'ar-SA'
  }
  if (locale.indexOf('id') > -1) {
    return 'id-ID'
  }
  if (locale.indexOf('pt') > -1) {
    return 'pt-BR'
  }
  if (locale.indexOf('ur') > -1) {
    return 'ur-PK'
  }
  return 'en-US'
}

let resolveI18nReady: () => void
const i18nReady = new Promise<void>((resolve) => {
  resolveI18nReady = resolve
})

async function applyLocalResources() {
  const lang = getLocale()
  const promiseArr = loadLocalI18nJson(lang)
  const modules = await Promise.all(promiseArr)

  const res = modules.reduce((acc, cur) => {
    return deepMerge(acc, cur.default)
  }, {})

  if (res) {
    i18n.addResources(lang, 'translation', res)
    // addResources 默认不会触发组件更新，手动通知已挂载订阅者
    i18n.emit('languageChanged', lang)
  }
  store.setStore({ name: STORE_LANG_KEY, content: lang, type: 'local' })
}

i18n.use(initReactI18next).init(
  {
    lng: getLocale(), // 默认语言
    fallbackLng: 'en-US', // 如果当前语言没有对应的翻译，将使用该语言作为备用
    interpolation: {
      escapeValue: false // 不要对翻译的文本进行转义，以支持 HTML 标签
    },
    react: {
      // 资源异步写入后也能驱动 useTranslation 重渲染
      bindI18nStore: 'added removed'
    }
  },
  async (err) => {
    try {
      if (err) {
        console.error(err)
        return
      }
      await applyLocalResources()
    } finally {
      resolveI18nReady()
    }
  }
)

export default i18n
export { getLocale, i18nReady }
